import React, { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, ShieldAlert, ShieldX, Check, X, Globe, KeyRound } from "lucide-react";
import { useApp, API } from "../AppContext";
import { PROGERE_LEGAL } from "../config/progere-legal";
import ProgereStoreQR from "../components/ProgereStoreQR";

/**
 * AppSecurityPage — /app/security
 * Affiche le badge d'authenticité de la session courante et les
 * informations d'intégrité (équivalent web du badge mobile PROGÈRE).
 * Le statut est retourné PAR LE SERVEUR (jamais calculé côté client).
 */
export default function AppSecurityPage() {
  const { lang, token } = useApp();
  const isFr = lang === "fr";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/mobile/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { setData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const T = isFr ? {
    tagline: "SÉCURITÉ DE L'APPLICATION",
    title: "PROGÈRE Devis AI",
    subtitle: "Statut d'authenticité et d'intégrité de votre session",
    verified: "APPLICATION VÉRIFIÉE",
    limited: "VÉRIFICATION LIMITÉE",
    unverified: "NON VÉRIFIÉE",
    domain: "Domaine officiel",
    session: "Session utilisateur",
    encryption: "Chiffrement",
    role: "Rôle",
    lastCheck: "Dernière vérification",
    checks: "Contrôles effectués",
    mobileNote: "Version mobile officielle bientôt disponible sur l'App Store et Google Play. Recherchez « PROGÈRE » par Groupe MJR Construction inc.",
    reportIssue: "Signaler un problème de sécurité",
    officialUrl: "URL officielle",
    ecosystem: "Écosystème",
  } : {
    tagline: "APPLICATION SECURITY",
    title: "PROGÈRE Devis AI",
    subtitle: "Authenticity and integrity status of your session",
    verified: "VERIFIED APPLICATION",
    limited: "LIMITED VERIFICATION",
    unverified: "NOT VERIFIED",
    domain: "Official domain",
    session: "User session",
    encryption: "Encryption",
    role: "Role",
    lastCheck: "Last check",
    checks: "Checks performed",
    mobileNote: "Official mobile version coming soon on the App Store and Google Play. Search for 'PROGÈRE' by Groupe MJR Construction inc.",
    reportIssue: "Report a security issue",
    officialUrl: "Official URL",
    ecosystem: "Ecosystem",
  };

  const statusMeta = {
    verified: { Icon: ShieldCheck, color: "var(--pg-success)", label: T.verified },
    limited: { Icon: ShieldAlert, color: "var(--pg-warning)", label: T.limited },
    unverified: { Icon: ShieldX, color: "var(--pg-danger)", label: T.unverified },
  };
  const meta = statusMeta[data?.status || "verified"];
  const Icon = meta.Icon;

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto py-12" data-testid="page-security-loading">
        <div className="rounded-2xl p-8 text-center" style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)", color: "var(--pg-text-muted)" }}>
          …
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="page-security">
      {/* Hero */}
      <div>
        <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--pg-gold)" }}>{T.tagline}</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black mt-1" style={{ color: "var(--pg-text-primary)" }}>
          <span style={{ color: "var(--pg-gold)" }}>PRO</span><span style={{ color: "var(--pg-red)" }}>GÈRE</span>{" "}
          <span style={{ color: "var(--pg-text-primary)" }}>DEVIS AI</span>
        </h1>
        <p className="mt-2 text-sm" style={{ color: "var(--pg-text-secondary)" }}>{T.subtitle}</p>
      </div>

      {/* Main badge */}
      <div
        data-testid="security-badge"
        className="rounded-2xl p-6"
        style={{
          background: "var(--pg-bg-card)",
          border: `1px solid color-mix(in srgb, ${meta.color} 45%, var(--pg-border))`,
          boxShadow: `0 12px 40px color-mix(in srgb, ${meta.color} 8%, transparent)`,
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background: `color-mix(in srgb, ${meta.color} 12%, transparent)`,
              border: `1px solid color-mix(in srgb, ${meta.color} 32%, transparent)`,
            }}
          >
            <Icon size={26} style={{ color: meta.color }} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: meta.color }}>
              {meta.label}
            </div>
            <div className="mt-1 text-lg font-bold" style={{ color: "var(--pg-text-primary)" }}>
              {isFr ? data?.message_fr : data?.message_en}
            </div>
            <div className="mt-1 text-xs" style={{ color: "var(--pg-text-muted)" }}>
              {T.lastCheck} : {new Date(data?.checked_at || Date.now()).toLocaleString(isFr ? "fr-CA" : "en-CA")}
            </div>
          </div>
        </div>
      </div>

      {/* Details grid */}
      <div className="rounded-2xl p-6" style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)" }}>
        <div className="text-[10px] uppercase font-semibold tracking-widest mb-4" style={{ color: "var(--pg-text-muted)" }}>{T.checks}</div>
        <div className="space-y-2">
          {(data?.checks || []).map((c, i) => (
            <div
              key={i}
              data-testid={`security-check-${i}`}
              className="flex items-center gap-3 py-2.5 border-b last:border-0"
              style={{ borderColor: "var(--pg-border)" }}
            >
              {c.ok ? (
                <Check size={16} style={{ color: "var(--pg-success)" }} />
              ) : (
                <X size={16} style={{ color: "var(--pg-danger)" }} />
              )}
              <span className="text-sm flex-1" style={{ color: "var(--pg-text-secondary)" }}>
                {isFr ? c.label_fr : c.label_en}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Session info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-xl p-4" style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)" }}>
          <div className="flex items-center gap-2 mb-2">
            <Globe size={14} style={{ color: "var(--pg-gold)" }} />
            <div className="text-[10px] uppercase font-semibold tracking-widest" style={{ color: "var(--pg-text-muted)" }}>{T.officialUrl}</div>
          </div>
          <div className="text-sm font-mono" style={{ color: "var(--pg-text-primary)" }}>devis.progere.ca</div>
          <div className="text-xs mt-1" style={{ color: "var(--pg-text-muted)" }}>{T.ecosystem} : PROGÈRE</div>
        </div>
        <div className="rounded-xl p-4" style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)" }}>
          <div className="flex items-center gap-2 mb-2">
            <KeyRound size={14} style={{ color: "var(--pg-gold)" }} />
            <div className="text-[10px] uppercase font-semibold tracking-widest" style={{ color: "var(--pg-text-muted)" }}>{T.session}</div>
          </div>
          <div className="text-sm truncate" style={{ color: "var(--pg-text-primary)" }}>{data?.user_email}</div>
          <div className="text-xs mt-1" style={{ color: "var(--pg-text-muted)" }}>{T.role} : <span className="uppercase font-semibold">{data?.session_role || "user"}</span></div>
        </div>
      </div>

      {/* Mobile teaser — QR codes stores */}
      <ProgereStoreQR variant="compact" />

      {/* Report */}
      <div className="text-center text-xs pt-4" style={{ color: "var(--pg-text-muted)" }}>
        {T.reportIssue} :{" "}
        <a href={`mailto:${PROGERE_LEGAL.dpo.email}?subject=S%C3%A9curit%C3%A9`} style={{ color: "var(--pg-gold)" }} data-testid="security-report-link">
          {PROGERE_LEGAL.dpo.email}
        </a>
      </div>
    </div>
  );
}
